import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Switch,
  Popconfirm,
  Modal,
  Form,
  Statistic,
  Row,
  Col,
  message,
  Descriptions,
  Drawer,
  Tooltip,
} from 'antd';
import type { AxiosError } from 'axios';
import {
  MobileOutlined,
  DesktopOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  GlobalOutlined,
  CalendarOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { adminService, LicensedDevice } from '../services/admin';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Option } = Select;
const { RangePicker } = DatePicker;

interface LicensedDeviceListProps {
  onViewDevice?: (deviceId: number) => void;
}

const LicensedDeviceList: React.FC<LicensedDeviceListProps> = ({ onViewDevice }) => {
  const [filters, setFilters] = useState({
    userId: undefined as number | undefined,
    isActive: undefined as boolean | undefined,
    deviceType: undefined as string | undefined,
    deviceId: '',
    expiresAfter: undefined as string | undefined,
    expiresBefore: undefined as string | undefined,
  });
  const [selectedDevice, setSelectedDevice] = useState<LicensedDevice | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      message.warning('当前未登录或登录已过期，请重新登录');
    }
  }, []);

  // Fetch device statistics
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['licensed-device-stats'],
    queryFn: adminService.getLicensedDeviceStats,
  });

  // Fetch licensed devices with filters
  const { data: devices, refetch, isLoading, error } = useQuery({
    queryKey: ['licensed-devices', filters],
    queryFn: () => {
      // Clean filters to avoid empty string parameters
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) =>
          value !== '' && value !== null && value !== undefined
        )
      );
      return adminService.getLicensedDevices(cleanFilters);
    },
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const getDeviceTypeIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'desktop':
        return <DesktopOutlined />;
      case 'mobile':
        return <MobileOutlined />;
      default:
        return <MobileOutlined />;
    }
  };

  const getDeviceTypeColor = (deviceType: string) => {
    switch (deviceType) {
      case 'desktop':
        return 'blue';
      case 'mobile':
        return 'green';
      default:
        return 'purple';
    }
  };

  const handleEdit = (device: LicensedDevice) => {
    setSelectedDevice(device);
    setEditModalVisible(true);
  };

  const handleView = (device: LicensedDevice) => {
    setSelectedDevice(device);
    setViewModalVisible(true);
  };

  const handleStatusChange = async (deviceId: number, newStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Authentication token missing. Please login again.');
        return;
      }

      await adminService.updateLicensedDevice(deviceId, { isActive: newStatus });
      message.success(`设备${newStatus ? '已激活' : '已停用'}`);
      refetch();
      refetchStats();
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        message.error('认证失败，请重新登录');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        const errorMessage = (axiosError.response?.data as any)?.message || '更新设备状态失败';
        message.error(errorMessage);
      }
    }
  };

  const handleDelete = async (deviceId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('认证令牌缺失，请重新登录');
        return;
      }

      await adminService.deleteLicensedDevice(deviceId);
      message.success('设备删除成功');
      refetch();
      refetchStats();
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        message.error('认证失败，请重新登录');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        const errorMessage = (axiosError.response?.data as any)?.message || '删除设备失败';
        message.error(errorMessage);
      }
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!selectedDevice) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('认证令牌缺失，请重新登录');
        return;
      }

      await adminService.updateLicensedDevice(selectedDevice.id, {
        isActive: values.isActive,
        expiresAt: values.expiresAt ? values.expiresAt.format('YYYY-MM-DDTHH:mm:ss') : undefined,
      });
      message.success('设备更新成功');
      setEditModalVisible(false);
      refetch();
      refetchStats();
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        message.error('认证失败，请重新登录');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        const errorMessage = (axiosError.response?.data as any)?.message || '更新设备失败';
        message.error(errorMessage);
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Device',
      key: 'device',
      render: (_: string, record: LicensedDevice) => (
        <Space>
          {getDeviceTypeIcon(record.deviceType)}
          <div>
            <div>{record.deviceName || record.deviceId}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.deviceType} - {record.deviceId}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'User',
      key: 'user',
      render: (_: string, record: LicensedDevice) => (
        <Space>
          <div>
            <div>{record.user.username}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.user.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: LicensedDevice) => (
        <Switch
          checked={isActive}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleStatusChange(record.id, checked)}
        />
      ),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (expiresAt: string) => expiresAt ? (
        <div>
          <div style={{ color: new Date(expiresAt) < new Date() ? 'red' : 'inherit' }}>
            {dayjs(expiresAt).format('YYYY-MM-DD HH:mm')}
          </div>
          <Tag color={new Date(expiresAt) < new Date() ? 'red' : 'green'}>
            {new Date(expiresAt) < new Date() ? 'Expired' : 'Active'}
          </Tag>
        </div>
      ) : <Tag color="blue">No Expiry</Tag>,
    },
    {
      title: 'Last Access',
      dataIndex: 'lastAccessTime',
      key: 'lastAccessTime',
      render: (lastAccessTime: string) => (
        <div>
          <div>{dayjs(lastAccessTime).format('YYYY-MM-DD HH:mm')}</div>
          {lastAccessTime && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              <GlobalOutlined /> {dayjs(lastAccessTime).fromNow()}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Last IP',
      dataIndex: 'lastAccessIP',
      key: 'lastAccessIP',
      render: (ip: string) => ip || <span style={{ color: '#ccc' }}>No IP</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: string, record: LicensedDevice) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            size="small"
            icon={<SyncOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个设备吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Devices"
              value={stats?.totalDevices || 0}
              prefix={<MobileOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Devices"
              value={stats?.activeDevices || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Expired Devices"
              value={stats?.expiredDevices || 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => { refetch(); refetchStats(); }}
              style={{ width: '100%', marginBottom: 8 }}
            >
              Refresh
            </Button>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {stats?.deviceTypeDistribution && Object.entries(stats.deviceTypeDistribution).map(([type, count]) => (
                <div key={type}>{type}: {count}</div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="授权设备管理">
        {error && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 4 }}>
            <div style={{ color: '#ff4d4f', marginBottom: 8 }}>数据加载失败</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {(error as AxiosError).response?.status === 401 ? '认证失败，请重新登录' :
               ((error as AxiosError).response?.data as any)?.message || '网络连接异常'}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <Input.Group compact style={{ marginBottom: 16 }}>
            <Input
              placeholder="Device ID"
              value={filters.deviceId}
              onChange={(e) => setFilters({...filters, deviceId: e.target.value})}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Device Type"
              value={filters.deviceType}
              onChange={(value) => setFilters({...filters, deviceType: value})}
              style={{ width: 120 }}
              allowClear
            >
              <Option value="desktop">Desktop</Option>
              <Option value="mobile">Mobile</Option>
              <Option value="web">Web</Option>
              <Option value="other">Other</Option>
            </Select>
            <Select
              placeholder="Status"
              value={filters.isActive}
              onChange={(value) => setFilters({...filters, isActive: value})}
              style={{ width: 100 }}
              allowClear
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Input.Group>
        </div>

        <Table
          dataSource={devices?.data || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} devices`,
          }}
        />
      </Card>

      {/* Edit Device Modal */}
      <Modal
        title={`编辑设备: ${selectedDevice?.deviceName || selectedDevice?.deviceId}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        {selectedDevice && (
          <Form
            layout="vertical"
            initialValues={{
              isActive: selectedDevice.isActive,
              expiresAt: selectedDevice.expiresAt ? dayjs(selectedDevice.expiresAt) : null,
            }}
            onFinish={handleEditSubmit}
          >
            <Form.Item
              label="Device Status"
              name="isActive"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
            <Form.Item
              label="Expiration Date (Optional)"
              name="expiresAt"
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select expiration date"
                format="YYYY-MM-DD HH:mm"
                showTime
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                >
                  Save Changes
                </Button>
                <Button onClick={() => setEditModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* View Device Details Modal */}
      <Modal
        title={`设备详情: ${selectedDevice?.deviceName || selectedDevice?.deviceId}`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {selectedDevice && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Device ID">{selectedDevice.deviceId}</Descriptions.Item>
            <Descriptions.Item label="Device Type">
              <Tag color={getDeviceTypeColor(selectedDevice.deviceType)}>
                {selectedDevice.deviceType}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Device Name">{selectedDevice.deviceName || 'Not specified'}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedDevice.isActive ? 'green' : 'red'}>
                {selectedDevice.isActive ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="User">{selectedDevice.user.username}</Descriptions.Item>
            <Descriptions.Item label="User Email">{selectedDevice.user.email}</Descriptions.Item>
            <Descriptions.Item label="Expires At">
              {selectedDevice.expiresAt ? dayjs(selectedDevice.expiresAt).format('YYYY-MM-DD HH:mm') : 'No expiry'}
            </Descriptions.Item>
            <Descriptions.Item label="Last Access">
              {dayjs(selectedDevice.lastAccessTime).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
            {selectedDevice.lastAccessIP && (
              <Descriptions.Item label="Last IP">{selectedDevice.lastAccessIP}</Descriptions.Item>
            )}
            {selectedDevice.lastAccessLocation && (
              <Descriptions.Item label="Last Location">{selectedDevice.lastAccessLocation}</Descriptions.Item>
            )}
            <Descriptions.Item label="Created">
              {dayjs(selectedDevice.createdAt).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default LicensedDeviceList;